import { authService } from 'features/auth/services/authService';
import { getAccessToken, setAccessToken } from 'core/store/authStore';
import type { OrderRealtimeEvent } from 'types/tracking.types';

const API_BASE =
  (import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/$/, '') +
  '/api/v1';

export async function connectOrderEvents(
  orderId: string,
  onEvent: (e: OrderRealtimeEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const run = async (token: string) => {
    const res = await fetch(`${API_BASE}/orders/${orderId}/events`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
      credentials: 'include',
    });
    if (res.status === 401) {
      const err = new Error('SSE unauthorized') as Error & { status?: number };
      err.status = 401;
      throw err;
    }
    if (!res.ok) throw new Error(`SSE failed: ${res.status}`);
    if (!res.body) throw new Error('SSE body missing');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const block of parts) {
        const line = block.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        try {
          onEvent(JSON.parse(line.slice(6)) as OrderRealtimeEvent);
        } catch {
          // ignore malformed events
        }
      }
    }
  };

  let token = getAccessToken();
  if (!token) throw new Error('SSE: no access token');

  try {
    await run(token);
  } catch (err: unknown) {
    const status = (err as { status?: number })?.status;
    if (status === 401) {
      const { accessToken } = await authService.refreshToken();
      setAccessToken(accessToken);
      token = accessToken;
      await run(token);
      return;
    }
    throw err;
  }
}
